import React from 'react'
import { connect } from 'react-redux'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import { generateId } from '@utilities/helper'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import {
  RECONNECTING_SCREEN_RESET_TIME_IN_MILLISECONDS,
  RECONNECTING_SCREEN_WAIT_TIME_IN_SECONDS,
} from '@configs/app'

export interface ComponentProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
}

function IdleTimeHandler({ session, projectIdentity }: ComponentProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const events = ['mousemove', 'click', 'keyup', 'touchstart', 'touchmove']

  const [, setLastAppUsageTime] = React.useState(Math.floor(Date.now() / 1000))
  const [appIdleKeyResetterId, setAppIdleKeyResetterId] =
    React.useState<ReturnType<typeof setTimeout>>()
  const [appIdleKeyFlag, updateAppIdleKeyFlag] = React.useState(false)
  const [appIdleKey, setAppIdleKey] = React.useState('')
  const [activeSessionId, setActiveSessionId] = React.useState('')
  const [eventRegistered, setEventRegistrationStatus] = React.useState(false)
  const [
    triggerProcessingWindowOnSessionSwitch,
    setTriggerProcessingWindowOnSessionSwitch,
  ] = React.useState(false)

  const updateAppIdleKey = (key = '') => {
    firebaseControlQuery.update({
      appIdleKey: key,
    })
  }

  const checkTimeElapsed = React.useCallback(() => {
    const currentTime = Math.floor(Date.now() / 1000)
    setLastAppUsageTime((prevTime) => {
      const differenceInSeconds = currentTime - prevTime
      updateAppIdleKeyFlag(
        differenceInSeconds > RECONNECTING_SCREEN_WAIT_TIME_IN_SECONDS
      )
      return currentTime
    })
  }, [])

  const handleActiveSessionProcessingWindow = () => {
    if (!activeSessionId) {
      setTriggerProcessingWindowOnSessionSwitch(false)
      return
    }

    if (!triggerProcessingWindowOnSessionSwitch) {
      setTriggerProcessingWindowOnSessionSwitch(true)
      return
    }

    updateAppIdleKey(generateId(10))
    setLastAppUsageTime(Math.floor(Date.now() / 1000))
  }

  const registerEvent = () => {
    events.forEach((event: string) =>
      window.addEventListener(event, checkTimeElapsed, true)
    )
    setEventRegistrationStatus(true)
  }

  const removeEvent = () => {
    events.forEach((event: string) =>
      window.removeEventListener(event, checkTimeElapsed, true)
    )
    setEventRegistrationStatus(false)
  }

  const handleEventRegistration = () => {
    const { sessionId, projectId } = projectIdentity

    setActiveSessionId(sessionId || '')

    if (!sessionId || !projectId) {
      removeEvent()
      return
    }

    if (!eventRegistered) {
      registerEvent()
    }
  }

  const resetAppIdleKeyTimer = () => {
    if (appIdleKeyResetterId) {
      clearTimeout(appIdleKeyResetterId)
      setAppIdleKeyResetterId(undefined)
    }
  }

  const setAppIdleKeyResetter = () => {
    if (!appIdleKey) {
      resetAppIdleKeyTimer()
      return
    }

    if (!appIdleKeyResetterId) {
      setAppIdleKeyResetterId(
        setTimeout(() => {
          updateAppIdleKey()
        }, RECONNECTING_SCREEN_RESET_TIME_IN_MILLISECONDS)
      )
    }
  }

  React.useEffect(() => {
    const { sessionId, projectId } = projectIdentity
    if (appIdleKeyFlag && sessionId && projectId) {
      updateAppIdleKey(generateId(10))
    }
  }, [appIdleKeyFlag])

  React.useEffect(() => {
    if (session) {
      const { appIdleKey: appIdleKeyFirebase } = session
      setAppIdleKey(appIdleKeyFirebase)
    }
  }, [session])

  React.useEffect(() => {
    handleActiveSessionProcessingWindow()
  }, [activeSessionId])

  React.useEffect(() => {
    handleEventRegistration()
  }, [projectIdentity])

  React.useEffect(() => {
    setAppIdleKeyResetter()
  }, [appIdleKey])

  if (appIdleKey === '') return null

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-90">
      <div className="absolute left-41% top-50% text-xl font-medium text-white">
        Re-establishing Connection, just a second…
      </div>
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
  })
)(IdleTimeHandler)
